import { createApi } from '@reduxjs/toolkit/query/react';

import deserialize from '../helpers/getroPropertyMapper';
import { networkBaseQueryV2 } from './queries';
import { decorateConnectionPathsMock } from './connectionPathsMock';

/**
 * Normalise the /connection_paths envelope into a flat array of path items.
 *
 * The shared deserializer coerces every JSON:API id with parseInt, but
 * connection_paths use synthetic string ids ("connpath:...") that parse to
 * NaN — which would make every card collide on the same React key + state.
 * Re-attach the original string id per item.
 */
export const transformConnectionPaths = (response) => {
  if (!response?.data) return [];
  const deserialized = deserialize(response);
  const items = deserialized.map((item, i) => ({
    ...item,
    id: response.data[i]?.id ?? item.id,
  }));
  // QA-only: synthesises strength fields behind the rsMock flag; a no-op otherwise.
  return decorateConnectionPathsMock(items);
};

/**
 * Connection Paths (v2)
 * Fetches ranked paths connecting the current team to a target contact.
 *
 * Only relationship_strength collections opt into the enriched read path; every
 * other collection keeps the exact legacy request (URL only, no params) so its
 * response is unchanged. When `withRelationshipStrength` is set we forward the
 * unified endpoint's knobs:
 *  - `impl=v2` — opt into the DeepFinder read path. Staff-only on the backend
 *    (`internal_staff? && impl==v2`); ignored for everyone else.
 *  - `max_depth=2` — covers direct (1 hop) + 2nd-degree (2 hops), the whole
 *    intro-paths UI surface. Capped at 2 so the legacy Finder never 422s
 *    (`max_depth > 2 requires ?impl=v2`).
 *  - `max_paths` — result cap (ignored by the legacy Finder).
 */
export const connectionPathsV2Api = createApi({
  reducerPath: 'connectionPathsV2Api',
  baseQuery: networkBaseQueryV2,
  endpoints: (builder) => ({
    getConnectionPaths: builder.query({
      query: ({ contactId, withRelationshipStrength = false }) => {
        const url = `/contacts/${contactId}/connection_paths`;
        // Flag-off collections issue the unchanged legacy request.
        if (!withRelationshipStrength) return { url };
        return { url, params: { impl: 'v2', max_depth: 2, max_paths: 100 } };
      },
      transformResponse: transformConnectionPaths,
    }),
  }),
});

export const { useGetConnectionPathsQuery, useLazyGetConnectionPathsQuery } = connectionPathsV2Api;
